import { useCallback, useMemo, useState } from 'react';
import { MAX_BULK_SIGN_BATCH_SIZE } from '@dealstack/shared/lib/common/eideasy/bulkSigning';
import type { ProgrammeSigningSelection } from './useProgrammeSigningSelection.types';

/**
 * Capped document selection for the authorised-signatory portal.
 *
 * Keyed by instance id, not signature id: a document the signatory holds two slots on shows
 * twice and must count once. Every output is derived through `selectableInstanceIds`, so a
 * document that leaves the list while ticked stops counting against the cap.
 */
export const useProgrammeSigningSelection = (
	selectableInstanceIds: string[],
	cap: number = MAX_BULK_SIGN_BATCH_SIZE
): ProgrammeSigningSelection => {
	const [heldInstanceIds, setHeldInstanceIds] = useState<string[]>([]);

	const selectable = useMemo(() => new Set(selectableInstanceIds), [selectableInstanceIds]);

	// Filtered on read, not pruned in state: a row that a refetch drops and restores keeps its
	// tick, and every reader sees the same number.
	const selectedInstanceIds = useMemo(
		() => heldInstanceIds.filter((instanceId) => selectable.has(instanceId)),
		[heldInstanceIds, selectable]
	);

	const selectedCount = selectedInstanceIds.length;
	const isAtCap = selectedCount >= cap;

	const selectedSet = useMemo(() => new Set(selectedInstanceIds), [selectedInstanceIds]);

	const isSelected = useCallback(
		(instanceId: string) => selectedSet.has(instanceId),
		[selectedSet]
	);

	const canSelect = useCallback(
		(instanceId: string) => selectedSet.has(instanceId) || !isAtCap,
		[selectedSet, isAtCap]
	);

	const toggle = useCallback(
		(instanceId: string) => {
			setHeldInstanceIds((current) => {
				if (current.includes(instanceId)) {
					return current.filter((id) => id !== instanceId);
				}
				// Counted inside the updater: several toggles can batch into one render, so a
				// render-time count would let all of them through. Ghosts are excluded, so a
				// document that has left the list cannot hold a slot shut.
				const selectedNow = current.filter((id) => selectable.has(id)).length;
				return selectedNow >= cap ? current : [...current, instanceId];
			});
		},
		[selectable, cap]
	);

	const clear = useCallback(() => setHeldInstanceIds([]), []);

	// Memoised: the column factory takes this object as a dependency, so a fresh identity on
	// every render would rebuild every column on every render.
	return useMemo(
		() => ({
			selectedInstanceIds,
			selectedCount,
			isAtCap,
			isSelected,
			canSelect,
			toggle,
			clear,
		}),
		[selectedInstanceIds, selectedCount, isAtCap, isSelected, canSelect, toggle, clear]
	);
};
